import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Activity,
  Globe,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import { macroAPI } from '../services/api';
import { MetricTooltip } from './MetricTooltip';

const MacroWidget = ({ selectedMarket, onMarketSelect }) => {
  const [macroData, setMacroData] = useState(null);
  const [allMacro, setAllMacro] = useState([]);
  const [allTrends, setAllTrends] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true); // Default expanded

  useEffect(() => {
    fetchMacroData();
  }, [selectedMarket]);

  const fetchMacroData = async () => {
    setLoading(true);
    try {
      // Fetch trends for all countries
      try {
        const trends = await macroAPI.getAllTrends();
        setAllTrends(trends || {});
      } catch (e) {
        console.error('Error fetching trends:', e);
      }

      if (selectedMarket && selectedMarket !== 'all') {
        // Use data from getAllTrends and filter by country code
        // (getTrends endpoint requires auth)
        const allData = await macroAPI.getAllTrends();
        // Map market codes (NSE, JK, PSE, WSE, KRX) to country codes (IN, ID, PH, PL, KR)
        const marketToCountryCode = { VN: 'VN', NSE: 'IN', IN: 'IN', PSE: 'PH', PH: 'PH', JK: 'ID', ID: 'ID', WSE: 'PL', PL: 'PL', KRX: 'KR', KR: 'KR' };
        const countryCode = marketToCountryCode[selectedMarket] || selectedMarket;
        const countryData = Array.isArray(allData) 
          ? allData.find(c => c.code === countryCode)
          : null;
        setMacroData(countryData);
      } else {
        const data = await macroAPI.getAllTrends();
        // API can return:
        // 1. { trends: [...], message: "..." } - extract trends array
        // 2. { countryCode: {...}, ... } - object with country codes as keys
        // 3. [...] - direct array
        let macroArray = [];
        
        if (data?.trends && Array.isArray(data.trends)) {
          // Case 1: { trends: [...] }
          macroArray = data.trends;
        } else if (Array.isArray(data)) {
          // Case 3: Direct array
          macroArray = data;
        } else if (data && typeof data === 'object') {
          // Case 2: Object with country codes - but skip known non-country keys
          const skipKeys = ['trends', 'message', 'error', 'detail'];
          const entries = Object.entries(data).filter(([key]) => !skipKeys.includes(key));
          if (entries.length > 0) {
            macroArray = entries.map(([code, info]) => ({
              country_code: code,
              ...info
            }));
          }
        }
        
        setAllMacro(macroArray);
        setMacroData(null);
      }
    } catch (err) {
      console.error('Error fetching macro data:', err);
      setAllMacro([]);
    } finally {
      setLoading(false);
    }
  };

  const getFreshnessColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-yellow-400';
    if (score >= 70) return 'text-orange-400';
    return 'text-red-400';
  };

  const getFreshnessBg = (score) => {
    if (score >= 90) return 'bg-emerald-500/20 border-emerald-500/30';
    if (score >= 80) return 'bg-yellow-500/20 border-yellow-500/30';
    if (score >= 70) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const getVerdictBadge = (countryCode) => {
    const trend = allTrends[countryCode];
    if (!trend || !trend.trends?.combined_verdict) return null;
    
    const verdict = trend.trends.combined_verdict;
    const colors = {
      'favorable': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'prudence': 'bg-red-500/20 text-red-400 border-red-500/30',
      'mixed': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'slowdown': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'neutral': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    
    return (
      <Badge className={`text-xs border ${colors[verdict.status] || colors.neutral}`}>
        {verdict.badge} {verdict.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="bg-white/5 border-white/10 mb-6" data-testid="macro-widget-loading">
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
            <span className="ml-2 text-gray-400">Chargement des données macro...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // All markets summary view
  if (!selectedMarket || selectedMarket === 'all') {
    // Don't render widget if no macro data available
    if (allMacro.length === 0) {
      return null;
    }
    
    return (
      <Card className="bg-white/5 border-white/10 mb-6" data-testid="macro-widget-summary">
        <div className="p-4 pb-2">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-400" />
            Contexte Macroéconomique
          </h3>
        </div>
        <CardContent className="p-4 pt-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {allMacro.map((country) => (
              <button
                key={country.code}
                onClick={() => onMarketSelect && onMarketSelect(country.code)}
                className="p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 transition-all text-left"
                data-testid={`macro-country-${country.code}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white">{country.code}</span>
                  {getVerdictBadge(country.code) || (
                    <MetricTooltip metric="freshnessScore">
                      <Badge className={`text-xs ${getFreshnessBg(country.macro_score || 0)}`}>
                        {country.macro_score ? `${Math.round(country.macro_score)}%` : 'N/A'}
                      </Badge>
                    </MetricTooltip>
                  )}
                </div>
                <div className="text-xs text-gray-400 mb-1">{country.country}</div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">{country.gdp_growth}%</span>
                  <MetricTooltip metric="gdpGrowth">
                    <span className="text-gray-500 text-xs">GDP</span>
                  </MetricTooltip>
                </div>
                <div className="mt-1 text-xs text-gray-500 truncate">
                  <MetricTooltip metric="cyclePhase">
                    {country.cycle_phase}
                  </MetricTooltip>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Single market detailed view
  if (!macroData) return null;

  // Use flat data structure from API (gdp_growth, macro_score, cycle_phase)
  const countryName = macroData.country_name || macroData.country || macroData.code;
  const gdpGrowth = macroData.gdp_growth;
  const macroScore = macroData.macro_score;
  const cyclePhase = macroData.cycle_phase;

  return (
    <Card className="bg-white/5 border-white/10 mb-6 overflow-hidden" data-testid="macro-widget-detail">
      {/* Clickable Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200 cursor-pointer"
        data-testid="macro-header-btn"
      >
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-emerald-400" />
          <span className="text-lg font-semibold text-white">
            {countryName} - Contexte Macro
          </span>
        </div>
        <div className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
          {expanded ? (
            <>
              <ChevronUp className="w-6 h-6" />
              <span className="text-sm font-medium">Masquer</span>
            </>
          ) : (
            <>
              <ChevronDown className="w-6 h-6" />
              <span className="text-sm font-medium">Voir +</span>
            </>
          )}
        </div>
      </button>

      {/* Expandable Content with Animation */}
      <div 
        className={`transition-all duration-200 ease-in-out overflow-hidden ${
          expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <CardContent className="p-4 pt-0 border-t border-white/10">
          {/* 3 Key Indicators: PIB, Score Macro, Phase du cycle */}
          <div className="grid grid-cols-3 gap-3">
            {/* PIB (GDP Growth) */}
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <MetricTooltip metric="gdpGrowth">
                <div className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> PIB
                </div>
              </MetricTooltip>
              <div className="text-lg font-semibold text-emerald-400">
                {gdpGrowth != null ? `${gdpGrowth}%` : '-'}
              </div>
            </div>

            {/* Score Macro */}
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <MetricTooltip metric="freshnessScore">
                <div className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <Activity className="w-3 h-3" /> Score Macro
                </div>
              </MetricTooltip>
              <div className={`text-lg font-semibold ${getFreshnessColor(macroScore)}`}>
                {macroScore != null ? `${Math.round(macroScore)}%` : '-'}
              </div>
            </div>

            {/* Phase du cycle */}
            <div className="p-3 rounded-lg bg-white/5 border border-white/10">
              <MetricTooltip metric="cyclePhase">
                <div className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" /> Phase du cycle
                </div>
              </MetricTooltip>
              <div className="text-sm font-semibold text-blue-400 capitalize">
                {cyclePhase || '-'}
              </div>
            </div>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default MacroWidget;
